/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { MED_DATETIME_NO_YEAR_DISPLAY, TIME_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { Theme } from '@coursera/cds-core';
import { Button, Grid, Link, Typography2 } from '@coursera/cds-core';
import { CloseIcon, SuccessIcon, VideoIcon } from '@coursera/cds-icons';
import type {
  AssignmentCalendarItem as Assignment,
  EventCalendarItem as LiveEvent,
} from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';
import {
  AssignmentCalendarItem_SubmissionType as AssignmentSubmissionType,
  AssignmentCalendarItem_ProgressState as ProgressState,
} from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';

import { humanizeTime } from 'bundles/author-course/utils/CourseMaterialUtils';
import { getItemTypePrefixTranslationByTypeName } from 'bundles/author-course/utils/ItemTypeUtils';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { RestoreSuccessNotification } from 'bundles/degree-home/components/v3-dpm/components/Calendar/RestoreSuccessNotification';
import ItemIcon from 'bundles/item/components/ItemIcon';
import type { ItemType } from 'bundles/item/types/ItemType';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/degree-home';

import Pill, { LivePill } from '../Pill';
import {
  DismissErrorNotification,
  DismissSuccessNotification,
  DismissWarningNotification,
} from './DismissNotifications';
import { RestoreButton } from './RestoreButton';
import type { FilterOptionValuesType } from './constants/constants';
import { filterOptionValues } from './constants/constants';

const styles: Record<string, Interpolation<Theme>> = {
  itemContainer: () => css`
    border-top: 1px solid var(--cds-color-grey-50);
    padding: 0.8rem 0 1rem;
  `,
  itemSubText: () => css`
    color: var(--cds-color-grey-600);
    font-size: 14px;
  `,
  itemDuration: () => css`
    color: var(--cds-color-grey-600);
    font-weight: normal;
    font-size: 14px;
  `,
  itemName: css`
    font-weight: normal;
  `,
  itemHeaderLine: css`
    display: inline;
  `,
  itemHeaderText: css`
    margin-right: 16px;
  `,
  itemPillContainer: css`
    display: inline-block;
  `,
  dotSeparator: () => css`
    margin: 0 var(--cds-spacing-100);
  `,
  gradeWeight: css`
    display: inline-block;
  `,
  courseName: css`
    a {
      color: inherit !important;
    }
  `,
  itemIcon: css`
    padding-left: 0 !important;
    padding-right: 0 !important;
  `,
  dismissButton: css`
    padding: 4px;
    margin-bottom: auto;
    position: static;
    max-width: 35px;
    width: 100%;

    & .cds-button-endIcon {
      margin-left: 0;
    }

    &:focus,
    &:hover {
      background: var(--cds-color-blue-600);
    }

    &:focus svg,
    &:hover svg {
      color: var(--cds-color-neutral-primary-invert);
    }
  `,
};

const dotSeparator = <span css={styles.dotSeparator}>•</span>;

const CalendarListItem = ({
  itemIcon,
  itemTypeName,
  itemName,
  itemUrl,
  duration,
  statusPill,
  subHeading,
  trackingName,
  courseId,
  itemId,
  isDismissible,
  isRestorable,
  itemFilterType,
  itemStatus,
}: {
  itemIcon: React.ReactElement;
  itemTypeName: string;
  itemName: string;
  itemUrl?: string;
  duration?: number;
  statusPill?: React.ReactElement | null;
  subHeading: React.ReactChild;
  trackingName: string;
  courseId: string;
  itemId: string;
  isDismissible?: boolean;
  isRestorable?: boolean;
  itemFilterType: FilterOptionValuesType;
  itemStatus?: string;
}) => {
  const { refetchCalendarItems, setRefetchRequired, filter } = useDegreeContext();
  const [showWarningModal, setWarningModal] = useState(false);
  const [showDismissSuccessModal, setDismissSuccessModal] = useState(false);
  const [showErrorModal, setErrorModal] = useState(false);

  // Used to hide the item if it's in the process of being dismissed
  // i.e. the request hasn't been sent to the server, or we haven't received an updated calendarItemsList with the dismissal
  const [isDismissed, setIsDismissed] = useState(false);

  const [showRestoreSuccessModal, setRestoreSuccessModal] = useState(false);

  const showModal = showWarningModal || showDismissSuccessModal || showErrorModal;

  const headerContents = (
    <React.Fragment>
      {itemTypeName}: <span css={styles.itemName}>{itemName}</span>
      {duration && <span css={styles.itemDuration}> {humanizeTime(duration)}</span>}
    </React.Fragment>
  );

  const hideOverdueItem = () => {
    setWarningModal(false);
    setDismissSuccessModal(true);
    setIsDismissed(true);
  };

  const hideOverdueItemError = () => {
    setWarningModal(false);
    setDismissSuccessModal(false);
    setErrorModal(true);
    setIsDismissed(false);
  };

  const dismissCalendarItemSuccess = () => {
    setDismissSuccessModal(false);
    refetchCalendarItems();
    setRefetchRequired(false);
  };

  const restoreCalendarItemSuccess = () => {
    setRestoreSuccessModal(false);
    refetchCalendarItems();
    setRefetchRequired(false);
  };

  return (
    <>
      {showWarningModal && filter === filterOptionValues.All && (
        <DismissWarningNotification
          courseId={courseId}
          itemId={itemId}
          onHide={hideOverdueItem}
          onCancel={() => setWarningModal(false)}
          onError={hideOverdueItemError}
          itemFilterType={itemFilterType}
        />
      )}
      {showDismissSuccessModal && (
        <DismissSuccessNotification onDismiss={dismissCalendarItemSuccess} itemFilterType={itemFilterType} />
      )}
      {showErrorModal && <DismissErrorNotification onDismiss={() => setErrorModal(false)} />}
      {showRestoreSuccessModal && <RestoreSuccessNotification onDismiss={restoreCalendarItemSuccess} />}
      {!isDismissed && (
        <div css={styles.itemContainer}>
          <Grid container spacing={12}>
            <Grid container item xs={1} justifyContent="center" css={styles.itemIcon}>
              {itemIcon}
            </Grid>
            <Grid container item xs={11} spacing={8}>
              <Grid container item xs={10} css={styles.itemHeaderLine}>
                {itemUrl ? (
                  <Link
                    component={TrackedA}
                    trackingName={trackingName}
                    href={itemUrl}
                    variant="quiet"
                    typographyVariant="subtitleMedium"
                    css={styles.itemHeaderText}
                    data-e2e={`${itemTypeName} link ${itemName}`}
                  >
                    {headerContents}
                  </Link>
                ) : (
                  <Typography2 component="h3" variant="subtitleMedium">
                    {headerContents}
                  </Typography2>
                )}

                {statusPill && <span css={styles.itemPillContainer}>{statusPill}</span>}
              </Grid>
              {isDismissible && (
                <Grid container item xs={2} justifyContent="flex-end" css={styles.itemIcon}>
                  {/* Update this button when the new CDS IconButton is released. */}
                  <Button
                    variant="ghost"
                    size="small"
                    disabled={showModal}
                    icon={<CloseIcon color="interactive" size="small" />}
                    onClick={() => setWarningModal(true)}
                    css={styles.dismissButton}
                    aria-label={_t('Dismiss #{itemTypeName}. #{itemName}. #{itemStatus}', {
                      itemName,
                      itemTypeName,
                      itemStatus: itemStatus ?? '',
                    })}
                    children=""
                  />
                </Grid>
              )}
              {isRestorable && (
                <Grid item xs={2} justifyContent="flex-end">
                  <RestoreButton
                    courseId={courseId}
                    itemId={itemId}
                    onClick={() => setRestoreSuccessModal(true)}
                    disabled={showRestoreSuccessModal}
                    onError={hideOverdueItemError}
                  />
                </Grid>
              )}
              <Grid item>
                <div css={styles.itemSubText}>{subHeading}</div>
              </Grid>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export const AssignmentItem = ({ item }: { item: Assignment }) => {
  const { filter } = useDegreeContext();
  let statusPill;
  let itemStatus;
  switch (item.progressState) {
    case ProgressState.PROGRESS_STATE_PASSED:
      if (item.grade !== undefined) {
        itemStatus = _t('Grade: #{grade}%', { grade: truncToPlace(item.grade * 100, 2) });
        statusPill = (
          <Pill
            rootClassName="grade-pill"
            data-e2e="grade-pill"
            label={itemStatus}
            textColor="var(--cds-color-green-700)"
            backgroundColor="var(--cds-color-green-50)"
          />
        );
      }
      break;
    case ProgressState.PROGRESS_STATE_FAILED:
      if (item.grade !== undefined) {
        itemStatus = _t('Grade: #{grade}%', { grade: truncToPlace(item.grade * 100, 2) });
        statusPill = (
          <Pill
            rootClassName="grade-pill"
            data-e2e="grade-pill"
            label={itemStatus}
            textColor="var(--cds-color-green-700)"
            backgroundColor="var(--cds-color-green-50)"
          />
        );
      }
      break;
    case ProgressState.PROGRESS_STATE_OVERDUE:
      if (item.dueAtTime) {
        itemStatus = _t('Overdue, #{dueAtTime}', {
          dueAtTime: formatDateTimeDisplay(item.dueAtTime, MED_DATETIME_NO_YEAR_DISPLAY),
        });
        statusPill = (
          <Pill
            rootClassName="overdue-pill"
            data-e2e="overdue-pill"
            label={itemStatus}
            textColor="var(--cds-color-yellow-700)"
            backgroundColor="var(--cds-color-yellow-50)"
          />
        );
      }
      break;
    default:
    // do nothing
  }

  const subHeading = (
    <span>
      <span css={styles.courseName}>
        <Link
          href={item.courseUrl}
          variant="quiet"
          data-e2e={`calendar-list-assignment-item-course-name-link ${item.courseName}`}
        >
          {item.courseName}
        </Link>
      </span>
      {item.progressState === ProgressState.PROGRESS_STATE_NOT_SUBMITTED && (
        <span>
          {dotSeparator}
          {_t('Due at #{dueAtTime}', { dueAtTime: formatDateTimeDisplay(item.dueAtTime || '', TIME_ONLY_DISPLAY) })}
        </span>
      )}
      {item.progressState === ProgressState.PROGRESS_STATE_SUBMITTED && (
        <span>
          {dotSeparator}
          {_t('Submitted')}
        </span>
      )}
      {item.gradingWeight !== undefined && (
        <span>
          {dotSeparator}
          <span css={styles.gradeWeight} data-e2e="calendar-list-item-grade-weight">
            {_t('#{gradingWeight}% Weight', { gradingWeight: truncToPlace(item.gradingWeight * 100, 2) })}
          </span>
        </span>
      )}
    </span>
  );

  const itemIcon = item.isComplete ? (
    <SuccessIcon color="success" size="large" title={_t('Completed')} aria-hidden={false} role="img" />
  ) : (
    <ItemIcon size={24} type={item.assignmentType as ItemType} useCdsIcon={true} cdsIconProps={{ size: 'large' }} />
  );

  const restorableAssignment =
    filter === filterOptionValues.Deadlines && item.progressState !== ProgressState.PROGRESS_STATE_OVERDUE;

  const isRestorable = item.isDismissed && (restorableAssignment || filter === filterOptionValues.Overdue);

  return (
    <CalendarListItem
      itemIcon={itemIcon}
      itemTypeName={getItemTypePrefixTranslationByTypeName(
        item.assignmentType as ItemType,
        item.assignmentType === 'staffGraded' || item.assignmentType === 'ungradedAssignment'
          ? {
              isTeamAssignment: item.submissionType === AssignmentSubmissionType.SUBMISSION_TYPE_TEAM,
              isGraded: item.assignmentType === 'staffGraded',
            }
          : undefined
      )}
      itemName={item.assignmentName}
      itemUrl={item.assignmentUrl}
      statusPill={statusPill}
      subHeading={subHeading}
      trackingName="degree_home_assignment_link"
      courseId={item.courseId}
      itemId={item.itemId}
      isDismissible={filter === filterOptionValues.All && !item.isDismissed}
      isRestorable={isRestorable}
      itemFilterType={
        item.progressState === ProgressState.PROGRESS_STATE_OVERDUE
          ? filterOptionValues.Overdue
          : filterOptionValues.Deadlines
      }
      itemStatus={itemStatus}
    />
  );
};

export const LiveEventItem = ({ item }: { item: LiveEvent }) => {
  const { filter } = useDegreeContext();
  const currentTime = Date.now();
  // start and end time types are undefined because of the autogenerated GRPC timestamp
  // however, in practice there are no live events without a start/end time
  const hasStarted = item.startTime && currentTime >= new Date(item.startTime).getTime();
  const hasEnded = item.endTime && currentTime > new Date(item.endTime).getTime();
  const isLive = hasStarted && !hasEnded;
  const startTime = formatDateTimeDisplay(item.startTime || '', TIME_ONLY_DISPLAY);
  const subHeading = (
    <>
      <span css={styles.courseName}>
        <Link
          href={item.courseUrl}
          variant="quiet"
          data-e2e={`calendar-list-live-event-item-course-name-link ${item.courseName}`}
        >
          {item.courseName}
        </Link>
      </span>
      {dotSeparator}
      {hasStarted ? _t('Started at #{startTime}', { startTime }) : _t('Starts at #{startTime}', { startTime })}
    </>
  );

  const eventDuration =
    item.startTime && item.endTime ? new Date(item.endTime).getTime() - new Date(item.startTime).getTime() : undefined;
  return (
    <CalendarListItem
      itemIcon={<VideoIcon size="large" />}
      itemTypeName={_t('Live Event')}
      itemName={item.eventTitle}
      itemUrl={!hasEnded ? item.eventUrl : undefined}
      duration={eventDuration}
      statusPill={isLive ? <LivePill /> : null}
      subHeading={subHeading}
      trackingName="degree_home_live_event_link"
      courseId={item.courseId}
      itemId={item.eventId}
      isDismissible={filter === filterOptionValues.All && !item.isDismissed}
      isRestorable={filter === filterOptionValues.LiveEvents && item.isDismissed}
      itemFilterType={filterOptionValues.LiveEvents}
    />
  );
};
